import { apiFetch, apiFetchBytes } from './client.js';

// #767 — the call logo as the SERVER fetched it (apps/api/src/routes/logo-snapshots.ts).
//
// The portal never loads a registrant's logo URL in the browser: that would tell
// the registrant which analyst opened their case, and when, and the CSP
// (`img-src 'self' data:`) refuses it anyway. The API fetches the logo, keeps
// every fetch, and serves the bytes to staff; this module reads them with the
// bearer token and hands back a `data:` URL, which is the only form the CSP lets
// an image take here besides a same-origin path the token would not reach.

export type LogoSnapshotOutcome = 'pending' | 'ok' | 'failed';

export interface LogoSnapshot {
  id: number;
  sourceUrl: string;
  /**
   * ROLLOUT (#768): the one-time baseline taken when logo re-vetting was deployed.
   * DRIFT_CHECK (#783): the daily re-fetch of an approved, delivered case's logo,
   * kept only when it differs from that URL's newest completed (settled) fetch.
   */
  trigger: 'PROMOTION' | 'CASE_CREATE' | 'URL_CHANGE' | 'REFETCH' | 'ROLLOUT' | 'DRIFT_CHECK';
  outcome: LogoSnapshotOutcome;
  sha256: string | null;
  contentType: string | null;
  byteLength: number | null;
  failureReason: string | null;
  requestedAt: string;
  fetchedAt: string | null;
  /** `users.id`, not the Keycloak sub; null on the automatic fetches. */
  fetchedBy: string | null;
  fetchedByName: string | null;
  /** True when these bytes differed from the reviewed logo and the server reset the SHAFT review. */
  resetShaftReview: boolean;
}

export interface LogoSnapshotHistory {
  caseId: string;
  logoUrl: string | null;
  shaftReviewed: boolean;
  /** The snapshot the SHAFT review was ticked against, stamped by the server. */
  reviewedSnapshotId: number | null;
  /** Newest first: `items[0]` is the current snapshot. */
  items: LogoSnapshot[];
}

/**
 * #810 — true while the case's newest snapshot is still `pending`, i.e. the
 * logo is being fetched and nobody has been shown it. The RCD/SHAFT tab will
 * not let the SHAFT review be ticked then, and the API refuses the tick with a
 * 422 on the same condition (`newestSnapshotIsPending`,
 * apps/api/src/lib/logo-snapshot.ts) — a behavioural pair, each side pinning
 * the other's predicate text. A FAILED newest snapshot is not a hold: there is
 * no image to have missed.
 */
export function logoFetchPending(history: Pick<LogoSnapshotHistory, 'items'> | null | undefined): boolean {
  return history?.items[0]?.outcome === 'pending';
}

/**
 * #767/#810 — how the portal waits for a queued snapshot: 2 s, then half as long
 * again each time, at most 15 s apart, for about two minutes in all. The worker
 * usually settles one in seconds, but a fetch may take a minute (four redirect
 * hops of 15 s each).
 */
export const LOGO_SNAPSHOT_POLL_DELAYS_MS: readonly number[] = (() => {
  const delays: number[] = [];
  let total = 0;
  for (let next = 2000; total < 120_000; next = Math.min(next * 1.5, 15_000)) {
    delays.push(next);
    total += next;
  }
  return delays;
})();

/**
 * #810 — wait, on that schedule, for the snapshot of `sourceUrl` to settle, and
 * return it (or null if it has not within about two minutes). Used after a new
 * logo URL is saved: the worker's settle bumps the case version when the image
 * is new, so the caller re-reads the case then, and the analyst's next edit
 * carries the current If-Match instead of 409ing.
 */
export async function waitForLogoSnapshot(caseId: string, sourceUrl: string): Promise<LogoSnapshot | null> {
  for (const delay of LOGO_SNAPSHOT_POLL_DELAYS_MS) {
    await new Promise((resolve) => setTimeout(resolve, delay));
    let history: LogoSnapshotHistory;
    try {
      history = await listLogoSnapshots(caseId);
    } catch {
      continue;
    }
    const newest = history.items[0];
    if (newest && newest.sourceUrl === sourceUrl && newest.outcome !== 'pending') return newest;
  }
  return null;
}

/** Every snapshot of the case's logo, newest first. */
export function listLogoSnapshots(caseId: string): Promise<LogoSnapshotHistory> {
  return apiFetch<LogoSnapshotHistory>(`/cases/${encodeURIComponent(caseId)}/logo-snapshots`);
}

/** "Re-fetch logo": the server fetches now and appends the result. */
export function refetchLogoSnapshot(
  caseId: string,
): Promise<{ snapshot: LogoSnapshot; shaftReviewReset: boolean }> {
  return apiFetch(`/cases/${encodeURIComponent(caseId)}/logo-snapshots`, { method: 'POST' });
}

/**
 * The only types an image is ever rendered as. A COPY of the API's
 * `LOGO_IMAGE_TYPES` (apps/api/src/lib/logo-snapshot.ts, which sniffs the bytes
 * into one of these four), tied by disk-read pins in both packages (#589,
 * docs/CROSS_PACKAGE_CONSTANTS.md) — logoSnapshots.test.ts here.
 */
export const LOGO_IMAGE_TYPES = ['image/bmp', 'image/png', 'image/jpeg', 'image/gif'] as const;

function base64(bytes: ArrayBuffer): string {
  const view = new Uint8Array(bytes);
  let binary = '';
  // Chunked: String.fromCharCode(...view) overflows the call stack on a 4 MiB logo.
  for (let i = 0; i < view.length; i += 0x8000) {
    binary += String.fromCharCode(...view.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/**
 * One snapshot as a `data:` URL for an `<img src>`. Refuses any type outside the
 * four image types rather than building a `data:` URL of whatever came back, so
 * the page can only ever be handed an image.
 */
export async function logoSnapshotDataUrl(caseId: string, snapshotId: number): Promise<string> {
  const { bytes, contentType } = await apiFetchBytes(
    `/cases/${encodeURIComponent(caseId)}/logo-snapshots/${snapshotId}/image`,
  );
  const type = contentType.split(';')[0].trim().toLowerCase();
  if (!(LOGO_IMAGE_TYPES as readonly string[]).includes(type)) {
    throw new Error(`logo snapshot ${snapshotId} is not an image (${type || 'no type'})`);
  }
  return `data:${type};base64,${base64(bytes)}`;
}
